import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Building2, Calendar, CheckCircle2, DollarSign, FileText, Mail, MapPin, PenTool, Phone, Target, User, X } from 'lucide-react';
import { Dialog, DialogContent } from '@mui/material';
import { DialogFooter } from './ui/dialog';
import ReactSignatureCanvas from 'react-signature-canvas';
import { getAuthHeader } from 'helpers/jwt-token-access/auth-token-header';
import api from 'Common/api';
import { getContractSignerType } from 'Common/permissions';
import './contract-detail-dialog.css';

interface Contract {
  id: string;
  projectName: string;
  projectDescription: string;
  projectAddress: string;
  contractorName: string;
  contractorCompany: string;
  contractorEmail: string;
  contractorPhone: string;
  contractAmount: number;
  startDate: string;
  estimatedCompletionDate: string;
  contractDate: string;
  contractorSignedDate: string;
  scope: string[];
}

interface ContractDetailDialogProps {
  contractID: any;
  onClose: () => void;
  onSign: (contractId: string) => void;
  selectedActionType?:any
  signerType?: "homeowner" | "contractor";
}

export function ContractDetailDialog({ contractID, selectedActionType, signerType, onClose, onSign }: ContractDetailDialogProps) {
  const [contract, setContract] = useState<Contract | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchedSignerType, setFetchedSignerType] = useState<"homeowner" | "contractor">("contractor");

 const [milstoneDetails,setMilestoneDetails]=useState([])
 
 useEffect(() => {
    const fetchContract = async () => {
      try {
        const data:any = await api.get(`/dashboard/contractReview/${contractID}`);
        if (data.result && data.result.length > 0) {
          const raw = data.result[0];
          setFetchedSignerType(getContractSignerType(raw));
          setMilestoneDetails(data.result)

          const mapped: Contract = {
            id: raw.pc_id,
            projectName: raw.project_name,
            projectDescription: raw.pc_work_description,
            projectAddress: `${raw.pc_address}, ${raw.pc_city}, ${raw.pc_state} ${raw.pc_zip}`,
            contractorName: `${raw.contractor_first_name} ${raw.contractor_last_name}`,
            contractorCompany: raw.contractor_company_name || 'N/A',
            contractorEmail: raw.contractor_email,
            contractorPhone: raw.contractor_phone,
            contractAmount: Number(raw.project_total_cost),
            startDate: raw.pc_start_date,
            estimatedCompletionDate: raw.pc_completion_date,
            contractDate: raw.contract_created_date,
            contractorSignedDate: raw.contractor_signed_date,
            scope: raw.pm_name ? [raw.pm_name] : [],
          };

          setContract(mapped);
        }
      } catch (error) {
        console.error('Error fetching contract:', error);
      }
    };

    if(contractID){
      fetchContract();
      setAgreed(false)
      setLoading(false)
      setIsSigning(false)
      handleClearSignature()
    }
    
  }, [contractID]);

  
  const [isSigning, setIsSigning] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const sigCanvasRef = useRef<ReactSignatureCanvas | null>(null);
  const signatureContainerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const container = signatureContainerRef.current;
    if (!contract || !container) return;

    const syncCanvasSize = () => {
      const signaturePad = sigCanvasRef.current;
      const canvas = signaturePad?.getCanvas();
      if (!signaturePad || !canvas) return;

      const bounds = canvas.getBoundingClientRect();
      const pixelRatio = Math.max(window.devicePixelRatio || 1, 1);
      const width = Math.max(1, Math.round(bounds.width * pixelRatio));
      const height = Math.max(1, Math.round(bounds.height * pixelRatio));

      if (canvas.width === width && canvas.height === height) return;

      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')?.scale(pixelRatio, pixelRatio);
      signaturePad.clear();
      setHasSignature(false);
    };

    syncCanvasSize();
    const resizeObserver = new ResizeObserver(syncCanvasSize);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [contract]);

  const handleSign = async () => {
    if (!hasSignature) return;
    
    setIsSigning(true);
    // Get signature data
    setLoading(true); // Set loading to true when submitting signature
      const signatureDataURL = sigCanvasRef.current?.toDataURL();
      if(!signatureDataURL){
        return
      }
      const signatureBlob = await fetch(signatureDataURL).then((res) =>
        res.blob()
      );
      const formData = new FormData();
      formData.append("file", signatureBlob, "signature.png");
      const headers = getAuthHeader();
      const resolvedSignerType = signerType || fetchedSignerType;
      try {
        const response = await api.post(
          `/contract/sign-contract/${resolvedSignerType}/${contractID}`,
          formData,
          {
            headers: {
              ...headers,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        if (response) {
  onSign(contractID)
   
        } else {
          console.error("Failed to upload signature");
        }
      } catch (error) {
        console.error("Error uploading signature:", error);
      } finally {
    setIsSigning(false);

        setLoading(false); // Set loading to false after submission completes
      }
  };

  const handleClearSignature = () => {
    sigCanvasRef.current?.clear();
    setHasSignature(false);
  };
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const handleSignatureEnd = () => {
      const isEmpty = sigCanvasRef.current?.isEmpty();
      setHasSignature(!isEmpty);
    };


  if (!contract) return <div>Loading contract...</div>;

  return (
    <Dialog
      open={true}
      onClose={isSigning || loading ? undefined : onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      className="tailwind-prefix contract-detail-dialog"
      aria-labelledby="contract-detail-title"
    >
      <DialogContent className="contract-detail-dialog__content">
        <div className="contract-detail-dialog__header">
          <div>
          <div id="contract-detail-title" className="flex items-center gap-2 font-[600] text-[16px]">
            <FileText className="w-5 h-5" />
            Contract Details{contract.id ? ` - #${contract.id}` : ''}
          </div>
          <div className="contract-detail-dialog__subtitle">Review the contract details and sign to proceed with the project</div>
          </div>
          <button
            type="button"
            className="contract-detail-dialog__close"
            onClick={onClose}
            disabled={isSigning || loading}
            aria-label="Close contract details"
          >
            <X size={18} />
          </button>
        </div>

        <div className="contract-detail-dialog__body space-y-5">
          {/* Project Info */}
          <div>
            <div className="text-gray-900 mb-3 mt-3 text-[14px]">Project Information</div>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="text-gray-600 text-sm ">Project Name</div>
              <div className="text-gray-900">{contract.projectName}</div>

              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <div className="text-gray-600 text-sm mb-1">Location</div>
                  <div className="text-gray-900">{contract.projectAddress}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="contract-detail-dialog__divider" />

          {/* Contractor Info */}
          <div>
            <div className="text-gray-900 mb-3 text-[14px]">Contractor Information</div>
            <div className="contract-detail-info-grid bg-gray-50 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <User className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-gray-600 text-sm mb-1">Contractor Name</p>
                  <p className="text-gray-900">{contract.contractorName}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Building2 className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-gray-600 text-sm mb-1">Company</p>
                  <p className="text-gray-900">{contract.contractorCompany}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-gray-600 text-sm mb-1">Email</p>
                  <p className="text-gray-900">{contract.contractorEmail}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-gray-600 text-sm mb-1">Phone</p>
                  <p className="text-gray-900">{contract.contractorPhone}</p>
                </div>
              </div>
              {contract?.contractorSignedDate && <div className="contract-detail-info-grid__full contract-detail-dialog__subdivider pt-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <div className="text-[12px] text-gray-900">Signed by contractor on {formatDate(contract.contractorSignedDate)}</div>
                </div>
              </div>}
           
            </div>
          </div>

          <div>
            <div className="text-gray-900 mb-3 text-[14px]">Milestone Details</div>
            <div className="space-y-3">
              {milstoneDetails.map((milestone:any, index) => (
                <div key={index} className="contract-detail-dialog__panel bg-gray-50 rounded-lg p-3">
                  <div className="flex items-start justify-between ">
                    <div className="flex items-start gap-2 flex-1">
                      <Target className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-gray-900">{milestone.milestone_name}</div>
                      </div>
                    </div>
                  </div>
                  <div className="contract-detail-dialog__subdivider grid grid-cols-2 mt-3 gap-4 pt-2">
                    <div>
                      <div className="text-gray-600 text-sm mb-1">Payment </div>
                      <div className="text-gray-900">{ formatCurrency(Number(milestone.contract_amount||0)*Number(milestone.milestone_percentage||0) /100) }</div>
                    </div>
                    <div>
                      <div className="text-gray-600 text-sm mb-1">Percentage</div>
                      <div className="text-gray-900">{milestone.milestone_percentage}%</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contract Terms */}
          <div>
            <div className="text-gray-900 mb-3 text-[14px]">Contract Terms</div>
            <div className="contract-detail-info-grid bg-gray-50 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <DollarSign className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <div className="text-gray-600 text-sm mb-1">Total Contract Amount</div>
                  <div className="text-gray-900">{formatCurrency(contract.contractAmount)}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <div className="text-gray-600 text-sm mb-1">Project Timeline</div>
                  <div className="text-gray-900">
                    {formatDate(contract.startDate)} - {formatDate(contract.estimatedCompletionDate)}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-gray-600 text-sm mb-1">Contract Date</div>
                <div className="text-gray-900">{formatDate(contract.contractDate)}</div>
              </div>
            </div>
          </div>


          {/* Scope of Work */}
          <div>
            <div className="text-gray-900 mb-3 text-[14px] flex items-center gap-2">
              <PenTool className="w-4 h-4" />
              Your Signature
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-gray-600 text-sm mb-3">
                Please sign below to confirm your agreement
              </p>
              <div ref={signatureContainerRef} className="contract-detail-dialog__signature bg-white rounded-lg border-2 border-gray-300">
               {!loading ? <ReactSignatureCanvas
                  ref={sigCanvasRef}
                  penColor="black"
                  canvasProps={{
                    className: "signature-canvas",
                  }}
                  onEnd={handleSignatureEnd}
                /> :<div>
                Uploading
                </div> }
               
              </div>
              <div className="mt-3 flex items-center justify-between">
                <p className="text-gray-500 text-sm">
                  {hasSignature ? 'Signature captured' : 'Draw your signature above'}
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleClearSignature}
                  disabled={!hasSignature}
                >
                  Clear Signature
                </Button>
              </div>
            </div>
          </div>

          {/* Agreement Checkbox */}
          <div className="contract-detail-dialog__agreement bg-blue-50 mb-3 rounded-lg p-3">
            <div className="flex items-start gap-3">
              <input
              type={'checkbox'}
                id="agree"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1"
              />
              <label htmlFor="agree" className="text-[11px] text-gray-900 cursor-pointer">
                I have reviewed the contract details and agree to the terms and conditions outlined above. 
                I understand that by signing this contract, I am entering into a legally binding agreement 
                with {contract.contractorCompany}.
              </label>
            </div>
          </div>
        </div>

        <DialogFooter className="contract-detail-dialog__footer">
          <Button  variant="outline" className='bg-transparent' onClick={onClose} disabled={isSigning||loading}>
            Cancel
          </Button>
          <Button variant={'secondary'} onClick={handleSign} disabled={!agreed || isSigning||loading}>
            {isSigning ? 'Signing...' : 'Sign Contract'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
